import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { dateIt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Trash2, ExternalLink, Paperclip, Camera } from "lucide-react";

const TIPI = { foto: "Foto", bolla: "Bolla di consegna", altro: "Altro" };

export function DocumentiCommessa({ commessaId }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [tipo, setTipo] = useState("foto");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["documenti", commessaId],
    queryFn: async () => (await api.get(`/commesse/${commessaId}/documenti`)).data,
  });

  const upMut = useMutation({
    mutationFn: async ({ f, t, n }) => {
      const fd = new FormData();
      fd.append("file", f);
      return (
        await api.post(`/commesse/${commessaId}/documenti`, fd, {
          params: { tipo: t, note: n },
          headers: { "Content-Type": "multipart/form-data" },
        })
      ).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documenti", commessaId] });
      setFile(null);
      setNote("");
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
      toast.success("Documento caricato");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const onCameraShot = (e) => {
    const f = e.target.files?.[0];
    if (f) upMut.mutate({ f, t: "foto", n: note || "Foto cantiere" });
  };

  const delMut = useMutation({
    mutationFn: async (docId) => (await api.delete(`/documenti/${docId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documenti", commessaId] });
      toast.success("Documento eliminato");
    },
  });

  const viewUrl = (d) => `${API}/documenti/${d.id}/download`;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-5">
      <div className="grid sm:grid-cols-6 gap-3 items-end">
        <div className="space-y-1.5 sm:col-span-2">
          <Label>File (foto o PDF)</Label>
          <Input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="bg-white cursor-pointer"
            data-testid="doc-file-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Tipo</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger data-testid="doc-tipo-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TIPI).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Note</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="es. Bolla n. 123 fornitore X" data-testid="doc-note-input" />
        </div>
        <Button
          onClick={() => (file ? upMut.mutate({ f: file, t: tipo, n: note }) : toast.error("Seleziona un file"))}
          disabled={upMut.isPending}
          data-testid="doc-upload-button"
        >
          <Upload className="h-4 w-4 mr-1" /> {upMut.isPending ? "Caricamento…" : "Carica"}
        </Button>
      </div>

      <div className="flex items-center gap-3 -mt-1">
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onCameraShot}
          data-testid="doc-camera-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => cameraRef.current?.click()}
          disabled={upMut.isPending}
          className="h-12 px-5 border-orange-300 text-primary hover:bg-orange-50 font-semibold"
          data-testid="doc-camera-button"
        >
          <Camera className="h-5 w-5 mr-2" /> Scatta foto cantiere
        </Button>
        <span className="text-xs text-slate-400 hidden sm:inline">Da smartphone apre la fotocamera e carica subito la foto.</span>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-slate-400">Caricamento…</div>
      ) : docs.length === 0 ? (
        <div className="py-10 text-center text-slate-400" data-testid="doc-empty">
          <Paperclip className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          Nessun documento allegato.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {docs.map((d) => {
            const isImg = d.content_type?.startsWith("image/");
            return (
              <div key={d.id} className="group border border-slate-200 rounded-xl overflow-hidden bg-slate-50 hover:border-primary transition-colors" data-testid={`doc-card-${d.id}`}>
                <a href={viewUrl(d)} target="_blank" rel="noreferrer" className="block aspect-[4/3] bg-white overflow-hidden" data-testid={`doc-open-${d.id}`}>
                  {isImg ? (
                    <img src={viewUrl(d)} alt={d.nome_file} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                      <FileText className="h-10 w-10 text-primary" />
                      <span className="text-xs mt-2">PDF</span>
                    </div>
                  )}
                </a>
                <div className="p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-primary uppercase tracking-wider">
                    {isImg ? <ImageIcon className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                    {TIPI[d.tipo] || d.tipo}
                  </div>
                  <div className="text-sm font-medium text-slate-800 truncate mt-0.5" title={d.nome_file}>{d.nome_file}</div>
                  {d.note && <div className="text-xs text-slate-500 truncate">{d.note}</div>}
                  <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                    <span>{dateIt(d.created_at)} · {d.caricato_da}</span>
                    <div className="flex items-center gap-2">
                      <a href={viewUrl(d)} target="_blank" rel="noreferrer" className="hover:text-primary" data-testid={`doc-view-${d.id}`}>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <button onClick={() => delMut.mutate(d.id)} className="hover:text-red-600" data-testid={`doc-delete-${d.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
